import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';

// Reducers
import { commentsReducer } from 'app/modules/comments/reducer';
import { immerRulesReducer } from 'app/modules/rules/reducer';
import { searchReducer } from 'app/modules/search/reducer';
import { workflowButtonConfigReducer } from 'app/modules/workflowButtonConfig/reducer';
import { insightsReducer } from 'app/modules/insights/reducer';
import { eventsReducer } from 'app/shared/eventsOld/reducer';
import { blacklistsReducer } from 'app/modules/lists/reducer';
import { alertsReducer } from 'app/modules/alerts/reducer';
import devtoolsReducer, {
  DEVTOOLS_SLICE_NAME,
} from 'app/modules/devtools/sliceDevtools';
import { toastsReducer } from 'app/shared/toasts/reducer';
import { uploadsReducer } from 'app/modules/uploads/reducer';
import { teamReducer } from 'app/modules/teamsOld/reducer';
import { dropdownReducer } from 'app/shared/dropdown/reducer';
import { goAMLSettingsReducer } from 'app/modules/goAMLSettings/reducer';
import { casesReducer } from 'app/modules/casesOld/reducer';
import { loadingReducer } from 'app/modules/loading/reducer';
import { navigatorReducer } from 'app/modules/navigator/reducer';
import { entitiesReducer } from 'app/modules/entitiesOld/reducer';
import {
  initialState as sessionInitialState,
  sessionReducer,
} from 'app/modules/session/reducer';
import { customChecklistReducer } from 'app/shared/customChecklist/reducer';
import investigationChecklistReducer, {
  investigationChecklistSliceName,
} from 'app/modules/investigationChecklist/sliceInvestigationChecklist';
import { featureFlagsReducer } from 'app/shared/featureFlags/reducer';
import { detectionModelsReducer } from 'app/modules/detectionModels/reducer';
import { permissionsReducer } from 'app/modules/permissions/reducer';
import { tableConfigReducer } from 'app/shared/CustomConfig/reducer';
import { goAMLReducer } from 'app/modules/goAML/reducer';
import sidebarReducer, { SIDEBAR_SLICE_NAME } from 'app/modules/sidebar/slice';
import casesSliceReducer, {
  CASES_SLICE_NAME,
} from 'app/modules/cases/sliceCases';
import investigationsReducer, {
  INVESTIGATIONS_SLICE_NAME,
} from 'app/modules/investigations/sliceInvestigations';
import flatFileIngestionReducer, {
  FFIP_NAME,
} from 'app/modules/pullBasedDataFiles/slicePullBasedDataFiles';
import dataMappingReducer, {
  DATA_MAPPING_NAME,
} from 'app/modules/dataMapping/sliceDataMapping';
import notificationsSliceReducer, {
  NOTIFICATIONS_SLICE_NAME,
} from 'app/modules/notifications/sliceNotifications';
import agentsSliceReducer, {
  AGENTS_SLICE_NAME,
} from 'app/modules/agents/sliceAgents';
import searchSliceReducer, {
  SEARCH_SLICE_NAME,
} from 'app/modules/search/sliceSearch';
import detectionModelsSliceReducer, {
  DETECTION_MODELS_SLICE_NAME,
} from 'app/modules/detectionModels/sliceDetectionModels';
import detectionModelDetailsSliceReducer, {
  DETECTION_MODEL_DETAILS_SLICE_NAME,
} from 'app/modules/detectionModelDetails/sliceDetectionModelDetails';
import relationshipsSliceReducer, {
  RELATIONSHIPS_SLICE_NAME,
} from 'app/modules/relationships/sliceRelationships';
import tagsSliceReducer, { TAGS_SLICE_NAME } from 'app/modules/tags/sliceTags';
import eventsSliceReducer, {
  EVENTS_SLICE_NAME,
} from 'app/shared/eventsOld/sliceEvents';
import teamsSliceReducer, {
  TEAMS_SLICE_NAME,
} from 'app/modules/teams/sliceTeams';
import apiKeysSliceReducer, {
  API_KEYS_SLICE_NAME,
} from 'app/modules/apiKeys/sliceApiKeys';
import orgSettingsSliceReducer, {
  ORG_SETTINGS_SLICE_NAME,
} from 'app/modules/orgSettings/sliceOrgSettings';
import themeSliceReducer, {
  THEME_SLICE_NAME,
} from 'app/modules/theme/sliceTheme';

import queuesSliceReducer, {
  QUEUES_SLICE_NAME,
} from 'app/modules/queues/sliceQueues';
import {
  name as dataSettingsName,
  reducer as dataSettingsReducer,
} from 'app/modules/dataSettings/sliceDataSettings';
import {
  name as settingsImportExportName,
  reducer as settingsImportExportReducer,
} from 'app/modules/settingsImportExport/sliceSettingsImportExport';
import {
  name as tableSettingsName,
  reducer as tableSettingsReducer,
} from 'app/modules/tableSettings/sliceTableSettings';
import {
  name as summarySettingsName,
  reducer as summarySettingsReducer,
} from 'app/modules/summarySettings/sliceSummarySettings';
import { history } from 'app/store/browserHistory';
import {
  getImpersonateId,
  getOriginalAgentId,
} from 'app/shared/utils/sessionStorage';
import sarsSliceReducer, {
  SARS_SLICE_NAME,
} from 'app/modules/fincenSarOld/sliceSars';
import orgSwitcherReducer, {
  ORG_SWITCHER_SLICE_NAME,
} from 'app/modules/orgSwitcher/sliceOrgSwitcher';
import rulesAdminReducer, {
  RULES_ADMIN_NAME,
} from 'app/modules/rulesAdmin/sliceRulesAdmin';
import attachmentsSliceReducer, {
  ATTACHMENTS_SLICE_NAME,
} from 'app/modules/attachments/sliceAttachments';
import entitiesSliceReducer, {
  ENTITIES_SLICE_NAME,
} from 'app/modules/entities/sliceEntities';
import {
  aiAgentsReducer,
  AI_AGENTS_SLICE_NAME,
} from 'app/modules/aiAgents/sliceAiAgents';
import filingsSliceReducer, {
  FILINGS_SLICE_NAME,
} from 'app/modules/filings/sliceFilings';
import sessionSliceReducer, {
  SESSION_SLICE_NAME,
} from 'app/modules/session/sliceSession';
import fincenSarReducer, {
  FINCEN_SAR_SLICE_NAME,
} from 'app/modules/fincenSAR/sliceFincenSar';
import sliceFincenCtr, {
  FINCEN_CTR_SLICE_NAME,
} from 'app/modules/fincenCtr/sliceFincenCtr';
import alertsSliceReducer, {
  ALERTS_SLICE_NAME,
} from 'app/modules/alerts/sliceAlerts';
import narrativeTemplatesSliceReducer, {
  NARRATIVE_TEMPLATES_SLICE_NAME,
} from 'app/modules/narrativeTemplates/sliceNarrativeTemplates';
import sliceSidePanel, {
  SIDE_PANEL_NAME,
} from 'app/shared/components/SidePanel/sliceSidePanel';
import instrumentReducer, {
  INSTRUMENTS_SLICE_NAME,
} from 'app/modules/instruments/sliceInstruments';
import transactionSliceReducer, {
  TRANSACTIONS_SLICE_NAME,
} from 'app/modules/transactions/sliceTransactions';
import {
  name as gaBannerSliceName,
  reducer as gaBannerReducer,
} from 'app/shared/components/GaBanner/sliceGaBanner';
import actionEventsSliceReducer, {
  ACTION_EVENTS_SLICE_NAME,
} from 'app/modules/actionEvents/sliceActionEvents';
import txnAnalysisSliceReducer, {
  TXN_ANALYSIS_SLICE_NAME,
} from 'app/modules/txnAnalysis/sliceTxnAnalysis';
import watchlistsSliceReducer, {
  WATCHLISTS_SLICE_NAME,
} from 'app/modules/watchlists/sliceWatchlists';
import fraudDashboardSliceReducer, {
  FRAUD_DASHBOARD_SLICE_NAME,
} from 'app/modules/fraudDashboard/slice';
import rtmRuleBuilderFormValuesSliceReducer, {
  RTM_RULE_BUILDER_FORM_VALUES_SLICE_NAME,
} from 'app/modules/detectionModels/sliceRtmRuleBuilderFormValues';
import {
  name as NOTES_SLICE_NAME,
  reducer as notesSliceReducer,
} from 'app/modules/notes/slice';
import reportDashboardSliceReducer, {
  REPORT_DASHBOARD_SLICE_NAME,
} from 'app/modules/reportDashboards/slice';
import scheduleSliceReducer, {
  SCHEDULE_SLICE_NAME,
} from 'app/modules/schedule/slice';
import workflowVisualizerSliceReducer, {
  NAME as WORKFLOW_VISUALIZER_SLICE_NAME,
} from 'app/modules/workflowVisualizer/slice';
import localTableSettingsReducer, {
  LOCAL_TABLE_SETTINGS_SLICE_NAME,
} from 'app/modules/localTableSettings/slice';

// Clear stale location state
if (history.location && history.location.state) {
  const state = {};
  history.replace({ ...history.location, state });
}

export const rootReducer = combineReducers({
  alerts: alertsReducer,
  [ALERTS_SLICE_NAME]: alertsSliceReducer,
  [ATTACHMENTS_SLICE_NAME]: attachmentsSliceReducer,
  blacklists: blacklistsReducer,
  casesOld: casesReducer,
  [CASES_SLICE_NAME]: casesSliceReducer,
  comments: commentsReducer,
  customChecklist: customChecklistReducer,
  [investigationChecklistSliceName]: investigationChecklistReducer,
  dropdown: dropdownReducer,
  entitiesOld: entitiesReducer,
  [ENTITIES_SLICE_NAME]: entitiesSliceReducer,
  [AI_AGENTS_SLICE_NAME]: aiAgentsReducer,
  events: eventsReducer,
  [FILINGS_SLICE_NAME]: filingsSliceReducer,
  [gaBannerSliceName]: gaBannerReducer,
  goAML: goAMLReducer,
  insights: insightsReducer,
  [INVESTIGATIONS_SLICE_NAME]: investigationsReducer,
  loading: loadingReducer,
  navigator: navigatorReducer,
  [NOTIFICATIONS_SLICE_NAME]: notificationsSliceReducer,
  permissions: permissionsReducer,
  [DEVTOOLS_SLICE_NAME]: devtoolsReducer,
  router: connectRouter(history),
  rules: immerRulesReducer,
  search: searchReducer,
  goAMLSettings: goAMLSettingsReducer,
  session: sessionReducer,
  [SESSION_SLICE_NAME]: sessionSliceReducer,
  [SIDE_PANEL_NAME]: sliceSidePanel,
  [SIDEBAR_SLICE_NAME]: sidebarReducer,
  team: teamReducer,
  toasts: toastsReducer,
  [THEME_SLICE_NAME]: themeSliceReducer,
  uploads: uploadsReducer,
  workflowButtonConfig: workflowButtonConfigReducer,
  detectionModels: detectionModelsReducer,
  tableConfig: tableConfigReducer,
  featureFlags: featureFlagsReducer,
  [FFIP_NAME]: flatFileIngestionReducer,
  [DATA_MAPPING_NAME]: dataMappingReducer,
  [AGENTS_SLICE_NAME]: agentsSliceReducer,
  [SEARCH_SLICE_NAME]: searchSliceReducer,
  [DETECTION_MODELS_SLICE_NAME]: detectionModelsSliceReducer,
  [DETECTION_MODEL_DETAILS_SLICE_NAME]: detectionModelDetailsSliceReducer,
  [RTM_RULE_BUILDER_FORM_VALUES_SLICE_NAME]:
    rtmRuleBuilderFormValuesSliceReducer,
  [RELATIONSHIPS_SLICE_NAME]: relationshipsSliceReducer,
  [TAGS_SLICE_NAME]: tagsSliceReducer,
  [EVENTS_SLICE_NAME]: eventsSliceReducer,
  [TEAMS_SLICE_NAME]: teamsSliceReducer,
  [API_KEYS_SLICE_NAME]: apiKeysSliceReducer,
  [ORG_SETTINGS_SLICE_NAME]: orgSettingsSliceReducer,
  [QUEUES_SLICE_NAME]: queuesSliceReducer,
  [dataSettingsName]: dataSettingsReducer,
  [SARS_SLICE_NAME]: sarsSliceReducer,
  [tableSettingsName]: tableSettingsReducer,
  [ORG_SWITCHER_SLICE_NAME]: orgSwitcherReducer,
  [RULES_ADMIN_NAME]: rulesAdminReducer,
  [FINCEN_SAR_SLICE_NAME]: fincenSarReducer,
  [FINCEN_CTR_SLICE_NAME]: sliceFincenCtr,
  [NARRATIVE_TEMPLATES_SLICE_NAME]: narrativeTemplatesSliceReducer,
  [summarySettingsName]: summarySettingsReducer,
  [settingsImportExportName]: settingsImportExportReducer,
  [INSTRUMENTS_SLICE_NAME]: instrumentReducer,
  [TRANSACTIONS_SLICE_NAME]: transactionSliceReducer,
  [ACTION_EVENTS_SLICE_NAME]: actionEventsSliceReducer,
  [TXN_ANALYSIS_SLICE_NAME]: txnAnalysisSliceReducer,
  [WATCHLISTS_SLICE_NAME]: watchlistsSliceReducer,
  [FRAUD_DASHBOARD_SLICE_NAME]: fraudDashboardSliceReducer,
  [NOTES_SLICE_NAME]: notesSliceReducer,
  [REPORT_DASHBOARD_SLICE_NAME]: reportDashboardSliceReducer,
  [SCHEDULE_SLICE_NAME]: scheduleSliceReducer,
  [WORKFLOW_VISUALIZER_SLICE_NAME]: workflowVisualizerSliceReducer,
  [LOCAL_TABLE_SETTINGS_SLICE_NAME]: localTableSettingsReducer,
});

// really nifty method for resetting the most of the entire redux state
// https://alligator.io/redux/reset-state-redux/
const wrappedRootReducer = (state: RootState, action) => {
  let newState = { ...state } as Partial<RootState>;
  if (action.type === '@@rootReducer/RESET_APP') {
    const impersonateId = getImpersonateId();
    const originalAgentId = getOriginalAgentId();
    newState = {
      featureFlags: state.featureFlags,
      session: {
        ...sessionInitialState,
        impersonating:
          impersonateId !== -1 && impersonateId !== originalAgentId,
      },
    } as any;
  }

  return rootReducer(newState as RootState, action);
};

export type RootState = ReturnType<typeof rootReducer>;
export default wrappedRootReducer;
